// Dates: the days to remember that are not things to do. Countdowns lead,
// as big numbers, because counting down is their point; then everything
// else by how soon, in plain words. Nothing is counted as missed: a one-off
// that has passed moves to a short "Gone by" list and stays out of the way.
import { useState, type ReactNode } from 'react'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useDates } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import { dateOf } from '@/lib/time'
import { repeatPhrase, upcomingDates, type Upcoming } from '@/lib/dates'
import { dateHue, tintText } from '@/lib/colour'
import type { DateResource } from '@/kinds/date/apply'
import { DateLeaf, DateRow, DateSheet } from '@/components/DateBits'
import { Button, Empty, PageTitle, SectionTitle } from '@/components/ui'

const LATER_CAP = 12

export const DatesView = () => {
  const navigate = useNavigate()
  const { id } = useParams({ strict: false }) as { id?: string }
  const { who, on } = useSearch({ strict: false }) as { who?: string; on?: string }
  const dates = useDates()
  const today = dateOf(new Date(useNow(60_000)))
  const [allLater, setAllLater] = useState(false)
  const close = () => void navigate({ to: '/dates' })

  const coming = upcomingDates(dates, today, 400)
  const countdowns = coming.filter((u) => u.date.spec.category === 'countdown')
  const rest = coming.filter((u) => u.date.spec.category !== 'countdown')
  const soon = rest.filter((u) => u.days <= 7)
  const month = rest.filter((u) => u.days > 7 && u.days <= 31)
  const later = rest.filter((u) => u.days > 31)
  const listed = new Set(coming.map((u) => u.date.id))
  const gone = dates
    .filter((d) => !listed.has(d.id))
    .sort((a, b) => (a.spec.date < b.spec.date ? 1 : -1))
    .slice(0, 5)
  const editing = id && id !== 'new' ? dates.find((d) => d.id === id) : undefined

  return (
    <div className="flex flex-col gap-5" data-testid="dates">
      <PageTitle
        right={
          <Button
            variant="primary"
            onClick={() => void navigate({ to: '/dates/$id', params: { id: 'new' } })}
            data-testid="date-new"
          >
            <Plus size={16} /> Add a date
          </Button>
        }
      >
        Dates
      </PageTitle>

      {dates.length === 0 && (
        <Empty icon="🗓">
          Birthdays, appointments, renewals, a trip to count down to. Each one reminds you ahead and on the
          day, and none of them is a task.
        </Empty>
      )}

      {countdowns.length > 0 && (
        // two to a row; an odd last one (or a lone one) takes the whole row rather than leaving a hole
        <section
          className="grid grid-cols-2 gap-3 [&>:last-child:nth-child(odd)]:col-span-2"
          data-testid="countdowns"
        >
          {countdowns.map((u) => (
            <Countdown key={u.date.id} u={u} />
          ))}
        </section>
      )}

      <Group title="This week" items={soon} today={today} />
      <Group title="This month" items={month} today={today} />
      <Group
        title="Later"
        items={allLater ? later : later.slice(0, LATER_CAP)}
        today={today}
        more={
          !allLater && later.length > LATER_CAP ? (
            <button type="button" className="text-[13px] text-accent-text" onClick={() => setAllLater(true)}>
              Show all
            </button>
          ) : undefined
        }
      />
      {gone.length > 0 && (
        <section>
          <SectionTitle>Gone by</SectionTitle>
          <ul className="group-list opacity-70">
            {gone.map((d) => (
              <DateRow key={d.id} date={d} on={d.spec.date} today={today} />
            ))}
          </ul>
        </section>
      )}

      {id === 'new' && <DateSheet personId={who} onDone={close} />}
      {editing && <DateSheet key={editing.id} resource={editing} on={on} onDone={close} />}
    </div>
  )
}

const Group = ({
  title,
  items,
  today,
  more,
}: {
  title: string
  items: Upcoming<DateResource>[]
  today: string
  more?: ReactNode
}) =>
  items.length ? (
    <section>
      <SectionTitle right={more}>{title}</SectionTitle>
      <ul className="group-list">
        {items.map((u) => (
          <DateRow key={u.date.id} date={u.date} on={u.on} today={today} />
        ))}
      </ul>
    </section>
  ) : null

/** A countdown as its number: the days to go, big, with the name under it. */
const Countdown = ({ u }: { u: Upcoming<DateResource> }) => {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={() => void navigate({ to: '/dates/$id', params: { id: u.date.id } })}
      className="card p-3.5 text-left flex flex-col gap-2 min-h-32"
      data-testid="countdown"
    >
      <div className="flex items-start justify-between">
        <span className="t-title tabular-nums leading-none" style={{ color: tintText(dateHue('countdown')) }}>
          {u.days === 0 ? 'Today' : u.days}
        </span>
        <DateLeaf on={u.on} category="countdown" small />
      </div>
      <span className="mt-auto">
        <span className="block text-[15px] font-medium truncate">{u.date.spec.title}</span>
        <span className="block text-xs text-faint">
          {u.days === 0 ? repeatPhrase(u.date.spec) : u.days === 1 ? 'day to go' : 'days to go'}
        </span>
      </span>
    </button>
  )
}
